import React, { useState } from 'react';
import { apiFetch } from '../api';

function MatchGroup({ group, groupKey }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-200 rounded" key={groupKey}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex justify-between items-center p-3 text-left hover:bg-slate-50"
      >
        <span className="text-sm font-medium text-slate-700 truncate">
          {group[0].filename}
        </span>
        <span className="text-xs text-slate-500 ml-2 shrink-0">
          {group.length} copies
        </span>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-3 bg-slate-50">
          {group.map((item, index) => (
            <div key={index} className="flex gap-4 text-xs text-slate-600 py-1">
              <span className="text-slate-400">Row {item.row_index + 1}</span>
              {item.folder && <span>Folder: {item.folder}</span>}
              {item.size && <span>Size: {item.size}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DuplicatesPanel({ duplicates }) {
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const exactGroups = duplicates.duplicate_groups || [];
  const exactGroupCount = duplicates.duplicate_group_count || 0;
  const exactAssetCount = duplicates.total_duplicate_assets || 0;
  const archiveCandidateCount = duplicates.archive_candidate_count
    ?? Math.max(0, exactAssetCount - exactGroupCount);
  const conflictGroups = duplicates.filename_conflict_groups || [];
  const conflictGroupCount = duplicates.filename_conflict_group_count || 0;
  const conflictAssetCount = duplicates.filename_conflict_asset_count || 0;
  const hasMatches = exactGroupCount > 0 || conflictGroupCount > 0;

  const createRecommendations = async () => {
    setCreating(true);
    setError(null);
    try {
      const response = await apiFetch('/rationalization/flag-duplicates', { method: 'POST' });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || data.message || 'Could not create duplicate recommendations');
      }
      setResult(data);
      setConfirming(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCreating(false);
    }
  };

  if (!hasMatches) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Duplicate Check</h3>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          No repeated filenames were found.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">Duplicate Check</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className={`rounded-lg border p-3 ${exactGroupCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
          <div className="text-xs font-semibold text-slate-700">Exact duplicate candidates</div>
          <div className="text-sm text-slate-600 mt-1">
            {exactAssetCount.toLocaleString()} assets in {exactGroupCount.toLocaleString()} groups
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Same filename and file size
          </div>
        </div>
        <div className={`rounded-lg border p-3 ${conflictGroupCount > 0 ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
          <div className="text-xs font-semibold text-slate-700">Filename matches to review</div>
          <div className="text-sm text-slate-600 mt-1">
            {conflictAssetCount.toLocaleString()} assets in {conflictGroupCount.toLocaleString()} groups
          </div>
          <div className="text-xs text-slate-500 mt-1">
            File sizes differ or are unavailable
          </div>
        </div>
      </div>

      {duplicates.note && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800 mb-4">
          {duplicates.note}
        </div>
      )}

      {exactGroupCount > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-baseline mb-2">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Exact matches</h4>
            <span className="text-xs text-amber-700">
              {archiveCandidateCount.toLocaleString()} extra {archiveCandidateCount === 1 ? 'copy' : 'copies'}
            </span>
          </div>
          <div className="space-y-2">
            {exactGroups.slice(0, 20).map((group, index) => (
              <MatchGroup key={`exact-${index}`} group={group} groupKey={`exact-${index}`} />
            ))}
          </div>
          {exactGroupCount > 20 && (
            <div className="text-xs text-slate-500 mt-3">
              Showing 20 of {exactGroupCount.toLocaleString()} groups
            </div>
          )}
        </div>
      )}

      {conflictGroupCount > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Needs review</h4>
          <div className="space-y-2">
            {conflictGroups.slice(0, 10).map((group, index) => (
              <MatchGroup key={`conflict-${index}`} group={group} groupKey={`conflict-${index}`} />
            ))}
          </div>
          {conflictGroupCount > 10 && (
            <div className="text-xs text-slate-500 mt-3">
              Showing 10 of {conflictGroupCount.toLocaleString()} groups
            </div>
          )}
        </div>
      )}

      {exactGroupCount > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          {result ? (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-800">
              {result.affected_assets > 0
                ? `Created ${result.archive_recommendations.toLocaleString()} Archive and ${result.keep_recommendations.toLocaleString()} Keep recommendations.`
                : 'No new recommendations were needed because these assets already have confirmed decisions.'}
              {' '}Review and accept them in Rationalize before export.
            </div>
          ) : confirming ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-xs text-amber-900 mb-2">
                Recommend {archiveCandidateCount.toLocaleString()} extra {archiveCandidateCount === 1 ? 'copy' : 'copies'} for Archive? One copy in each exact-match group will remain Keep. Nothing is excluded until you accept the recommendations in Rationalize.
              </p>
              {error && <p className="text-xs text-red-700 mb-2">{error}</p>}
              <div className="flex gap-2">
                <button
                  onClick={createRecommendations}
                  disabled={creating}
                  className="px-3 py-1 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  {creating ? 'Creating…' : 'Create recommendations'}
                </button>
                <button
                  onClick={() => { setConfirming(false); setError(null); }}
                  className="px-3 py-1 bg-white text-slate-600 text-xs font-medium rounded border hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-medium rounded border border-amber-200 hover:bg-amber-100 transition w-full"
            >
              Recommend exact duplicates for Archive →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
